import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import ElmActivator from "./elm-activator.js";
import Analytics from "./analytics.js";

function setupLiveViewSocket() {
  let csrfToken = document
    .querySelector("meta[name='csrf-token']")
    .getAttribute("content");
  let Hooks = {};

  let LoadTimesheet = {
    mounted() {
      ElmActivator.run(this.el, true);
    },
  };

  let LoadHeader = {
    mounted() {
      ElmActivator.run(this.el, false);
    },
  };

  // Hover tooltips for Client Trend Report chart segments (rect.chart-seg
  // carry data-* attributes). One shared tooltip element on <body>.
  Hooks.ChartTooltip = {
    tipEl() {
      let tip = document.getElementById("chart-seg-tip");
      if (!tip) {
        tip = document.createElement("div");
        tip.id = "chart-seg-tip";
        tip.style.cssText =
          "position:fixed;z-index:100;pointer-events:none;background:#1f2733;color:#fff;" +
          "border-radius:8px;padding:8px 10px;font-size:12px;white-space:nowrap;" +
          "box-shadow:0 4px 14px rgba(16,24,40,.25);opacity:0;transition:opacity .1s";
        document.body.appendChild(tip);
      }
      return tip;
    },
    move(e, tip) {
      tip.style.left =
        Math.min(e.clientX + 14, window.innerWidth - tip.offsetWidth - 10) + "px";
      tip.style.top = e.clientY + 16 + "px";
    },
    mounted() {
      const tip = this.tipEl();

      this.el.addEventListener("mouseover", (e) => {
        const seg = e.target.closest && e.target.closest(".chart-seg");
        if (!seg) return;
        const d = seg.dataset;

        // Build via DOM + CSSOM. The app's CSP strips inline `style`
        // attributes, so an innerHTML string with styles would lose the
        // colours and spacing — set styles as properties instead.
        tip.textContent = "";

        const header = document.createElement("div");
        header.style.cssText =
          "display:flex;align-items:center;gap:6px;font-weight:600;margin-bottom:4px";

        const swatch = document.createElement("i");
        swatch.style.cssText =
          "width:9px;height:9px;border-radius:2px;display:inline-block";
        swatch.style.background = d.color;

        const act = document.createElement("span");
        act.textContent = d.act;

        const month = document.createElement("span");
        month.textContent = d.month;
        month.style.cssText =
          "color:#aab2bd;font-weight:400;font-size:11px;margin-left:6px";

        header.append(swatch, act, month);

        const detail = document.createElement("div");
        detail.style.color = "#e6e9ee";
        detail.textContent = `${d.n} activities · ${d.dur}`;

        tip.append(header, detail);
        tip.style.opacity = "1";
        this.move(e, tip);
      });

      this.el.addEventListener("mousemove", (e) => {
        if (tip.style.opacity === "1" && e.target.closest?.(".chart-seg")) {
          this.move(e, tip);
        }
      });

      this.el.addEventListener("mouseout", (e) => {
        if (e.target.closest && e.target.closest(".chart-seg")) {
          tip.style.opacity = "0";
        }
      });
    },
    destroyed() {
      const tip = document.getElementById("chart-seg-tip");
      if (tip) tip.style.opacity = "0";
    },
  };

  Hooks.OpenFileNote = {
    fileNote() {
      return JSON.parse(this.el.dataset.fileNote);
    },
    mounted() {
      this.el.addEventListener("click", () => {
        if (window.FileNote) {
          window.FileNote.open(this.fileNote());
        }
      });
    },
  };

  Hooks.ConfirmInputChange = {
    mounted() {
      const isCheckbox = this.el.type === "checkbox";

      const initialValue = isCheckbox ? this.el.checked : this.el.value;
      this.el.setAttribute("data-initial-value", initialValue);

      this.el.addEventListener("change", (event) => {
        event.stopImmediatePropagation();
        event.preventDefault();

        const isCheckbox = this.el.type === "checkbox";
        const initialValue = this.el.getAttribute("data-initial-value");
        const newValue = isCheckbox ? this.el.checked : this.el.value;

        if (newValue === initialValue) {
          return;
        }

        const confirmChange = confirm(
          "This will be applied to all fee types. Are you sure?",
        );

        if (!confirmChange) {
          if (isCheckbox) {
            this.el.checked = !newValue;
          } else {
            this.el.value = initialValue || "";
          }
        } else {
          this.el.setAttribute("data-initial-value", newValue);

          this.pushEvent("batch-update-fee-types", {
            [this.el.name]: isCheckbox ? this.el.checked : this.el.value,
          });
        }
      });
    },
  };

  Hooks.LoadTimesheet = LoadTimesheet;
  Hooks.LoadHeader = LoadHeader;

  Hooks.TemplateEditor = {
    mounted() {
      this.textarea = this.el.querySelector("textarea");
      this.cursorPos = this.textarea ? this.textarea.value.length : 0;

      if (this.textarea) {
        const trackCursor = () => {
          this.cursorPos = this.textarea.selectionStart;
        };
        this.textarea.addEventListener("click", trackCursor);
        this.textarea.addEventListener("keyup", trackCursor);
        this.textarea.addEventListener("focus", trackCursor);
      }

      this.handleEvent("insert-variable", ({ variable }) => {
        if (!this.textarea) return;

        const before = this.textarea.value.substring(0, this.cursorPos);
        const after = this.textarea.value.substring(this.cursorPos);
        this.textarea.value = before + variable + after;
        this.cursorPos = this.cursorPos + variable.length;

        this.textarea.focus();
        this.textarea.setSelectionRange(this.cursorPos, this.cursorPos);
        this.textarea.dispatchEvent(new Event("input", { bubbles: true }));
      });
    },
  };

  Hooks.PreviewContent = {
    mounted() { this.el.textContent = this.el.dataset.content; },
    updated() { this.el.textContent = this.el.dataset.content; },
  };

  Hooks.AmplitudeEvent = {
    eventName() {
      return this.el.dataset.event;
    },
    eventProperties() {
      return JSON.parse(this.el.dataset.eventProperties);
    },
    mounted() {
      this.el.addEventListener("click", () => {
        Analytics.trackEvent(this.eventName(), this.eventProperties());
      });
    },
  };

  Hooks.NoEnter = {
    mounted() {
      this.el.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      });
    },
  };

  Hooks.SearchSelect = {
    items() {
      return Array.from(this.el.querySelectorAll("[data-search-item]"));
    },
    highlight(index) {
      const items = this.items();
      this.index = items.length === 0 ? 0 : (index + items.length) % items.length;
      items.forEach((item, i) => {
        if (i === this.index) {
          item.setAttribute("data-active", "");
          item.scrollIntoView({ block: "nearest" });
        } else {
          item.removeAttribute("data-active");
        }
      });
    },
    mounted() {
      this.index = 0;
      this.el.addEventListener("keydown", (e) => {
        if (e.target.tagName !== "INPUT") return;

        if (e.key === "Enter") {
          e.preventDefault();
          const item = this.items()[this.index];
          if (item) item.click();
        } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          this.highlight(this.index + (e.key === "ArrowDown" ? 1 : -1));
        }
      });
      this.highlight(0);
    },
    updated() {
      this.highlight(0);
    },
  };

  Hooks.SubmitForm = {
    mounted() {
      const renderedData = new FormData(this.el);

      this.handleEvent("submit_form", ({ form_id, format } = {}) => {
        const form = form_id ? document.getElementById(form_id) : this.el;

        if (!form) return;

        if (format !== "csv") {
          form.submit();
          return;
        }

        const exportForm = document.createElement("form");
        exportForm.method = "POST";
        exportForm.action = form.action + `?format=${format}`;
        exportForm.target = "_blank";
        exportForm.style.display = "none";

        for (const [name, value] of renderedData.entries()) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = name;
          input.value = value;
          exportForm.appendChild(input);
        }

        document.body.appendChild(exportForm);
        exportForm.submit();
        exportForm.remove();
      });
    },
  };

  Hooks.PMSSelect = {
    mounted() {
      const select = this.el;
      const configFields =
        this.el.parentElement.querySelectorAll(".config-field");

      const toggleConfigFields = (selectedValue) => {
        configFields.forEach((field) => {
          field.style.display =
            field.dataset.pmsValue === selectedValue ? "block" : "none";
        });
      };

      toggleConfigFields(select.value);

      select.addEventListener("change", (event) => {
        toggleConfigFields(event.target.value);
      });
    },
  };

  Hooks.DatalistBatchRateLabelSelect = {
    mounted() {
      this.el.addEventListener("input", () => {
        const listId = this.el.getAttribute("list");
        const datalist = document.getElementById(listId);
        if (!datalist) return;

        const match = Array.from(datalist.options).find(
          (o) => o.value === this.el.value,
        );
        if (!match) return;

        const initialValue = this.el.getAttribute("data-initial-value");
        const newValue = this.el.value;

        if (newValue === initialValue) return;

        const confirmChange = confirm(
          "This will be applied to all fee types. Are you sure?",
        );

        if (!confirmChange) {
          this.el.value = initialValue || "";
        } else {
          this.el.setAttribute("data-initial-value", newValue);
          this.pushEvent("batch-update-fee-types", {
            default_rate_label_id: match.dataset.id || null,
          });
        }
      });
    },
  };

  Hooks.DatalistRateLabelSelect = {
    mounted() {
      this.el.addEventListener("input", () => {
        const listId = this.el.getAttribute("list");
        const datalist = document.getElementById(listId);
        if (!datalist) return;

        const match = Array.from(datalist.options).find(
          (o) => o.value === this.el.value,
        );
        if (match) {
          this.pushEvent("update-fee-type", {
            fee_type_id: this.el.dataset.feeTypeId,
            default_rate_label_id: match.dataset.id || null,
          });
        }
      });
    },
  };

  Hooks.DatalistSelect = {
    mounted() {
      this.el.addEventListener("input", () => {
        const listId = this.el.getAttribute("list");
        const datalist = document.getElementById(listId);
        if (!datalist) return;

        const match = Array.from(datalist.options).find(
          (o) => o.value === this.el.value,
        );
        if (match) {
          const code = this.el.value.split(" : ")[0];
          this.el.value = code === "None" ? "" : code;

          this.pushEvent("update-fee-type", {
            fee_type_id: this.el.dataset.feeTypeId,
            pms_code: match.dataset.id || null,
          });
        }
      });
    },
  };

  let liveSocket = new LiveSocket("/live", Socket, {
    hooks: Hooks,
    params: {
      _csrf_token: csrfToken,
    },
  });

  // Connect if there are any LiveViews on the page
  liveSocket.connect();

  // Expose liveSocket on window for web console debug logs and latency simulation:
  // >> liveSocket.enableDebug()
  // >> liveSocket.enableLatencySim(1000)
  // The latency simulator is enabled for the duration of the browser session.
  // Call disableLatencySim() to disable:
  // >> liveSocket.disableLatencySim()
  window.liveSocket = liveSocket;
}

export default { setupLiveViewSocket };

// Progress Animations
// npm install --prefix assets --save topbar
// import topbar from "topbar"

// Show progress bar on live navigation and form submits
// topbar.config({barColors: {0: "#29d"}, shadowColor: "rgba(0, 0, 0, .3)"})
// window.addEventListener("phx:page-loading-start", info => topbar.show())
// window.addEventListener("phx:page-loading-stop", info => topbar.hide())
